/**
 * RunMatrixScreen — the accountant's day-of-work surface for a single run.
 *
 * A spreadsheet grid: rows = clients, columns = required documents,
 * cells = ✓/✗/→ with a tooltip showing the upload timestamp. Above the
 * grid, summary chips ("23/30 received") and a "missing only" toggle.
 * Per-row actions: send reminder (one-click; idempotent on the backend).
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  api,
  type ClientResponse,
} from "../lib/api";

interface Cell {
  document_label: string;
  status: "acked" | "received" | "archived" | "missing";
  file_id: string | null;
  uploaded_at: string | null;
}

interface Row {
  client_id: string;
  client_name: string;
  cells: Cell[];
  extra_docs: string[];
}

interface Matrix {
  run_id: string;
  period_label: string;
  deadline_at: string;
  document_labels: string[];
  rows: Row[];
  summary: { received: number; missing: number; clients: number };
}

interface RunMeta {
  closed_at: string | null;
}

const REMINDER_DAY_OPTIONS = [1, 2, 3, 5, 7, 10, 14, 21, 30];

const EXTRA_DOC_SUGGESTIONS = [
  "הפקדות לפנסיה, גמל, השתלמות, ביטוחים — אישורי מס או דוח מקוצר",
  "ביטוח עסקי — אישור מחברת ביטוח או פוליסת הביטוח",
  "פקדונות, השקעות בני\"ע — טופס 867",
  "אישורים על תרומות",
  "הכנסות נוספות או נכסים בחו\"ל",
  "תעודות זהות בנקאיות",
  "אישור שנתי להצהרת הון מכל הפנסיות וקרנות ההשתלמות",
  "אישורים על חשבונות ונכסים בחו\"ל",
  "חוזה רכישה על נכסים בישראל",
  "אישורים על יתרות הלוואות",
  "אישור יתרות של השקעות בניירות ערך / אג\"ח / מטבעות קריפטוגרפים ליום",
  "רישיון רכב",
];

const STATUS_COLORS: Record<Cell["status"], string> = {
  acked: "green",
  received: "blue",
  archived: "gray",
  missing: "red",
};

const STATUS_LABELS: Record<Cell["status"], string> = {
  acked: "התקבל",
  received: "הועלה",
  archived: "בארכיון",
  missing: "חסר",
};

export function RunMatrixScreen() {
  const { id } = useParams<{ id: string }>();
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [meta, setMeta] = useState<RunMeta | null>(null);
  const [campaignName, setCampaignName] = useState<string | null>(null);
  const [campaignDefaultSchedule, setCampaignDefaultSchedule] = useState<number[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missingOnly, setMissingOnly] = useState(false);
  const [busyClient, setBusyClient] = useState<string | null>(null);
  // Mid-run add modal state.
  const [addOpen, setAddOpen] = useState(false);
  const [allClients, setAllClients] = useState<ClientResponse[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [chosenClient, setChosenClient] = useState<string | null>(null);
  // Per-(run,client) settings modal state.
  const [settingsClient, setSettingsClient] = useState<{
    client_id: string;
    client_name: string;
  } | null>(null);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      // Run matrix + run row are independent — keep the matrix the
      // primary load and fetch run-meta in parallel so we know whether
      // to render Close or Reopen. Failing to load the meta isn't
      // fatal; we just hide the button.
      const [m, allRuns, campaignsList] = await Promise.all([
        api.runMatrix(id),
        api.runsList(true).catch(() => ({ runs: [] })),
        api.campaignsList().catch(() => ({ campaigns: [] })),
      ]);
      setMatrix(m);
      const r = allRuns.runs.find((x) => x.run_id === id);
      setMeta(r ? { closed_at: r.closed_at ?? null } : null);
      if (r) {
        const camp = campaignsList.campaigns.find((c) => c.campaign_id === r.campaign_id);
        setCampaignName(camp?.name ?? null);
        setCampaignDefaultSchedule(camp?.default_reminder_schedule_days ?? null);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError("קמפיין לא נמצא.");
      } else {
        setError("טעינת הקמפיין נכשלה.");
      }
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visibleRows = useMemo(() => {
    if (!matrix) return [];
    if (!missingOnly) return matrix.rows;
    return matrix.rows.filter((r) => r.cells.some((c) => c.status === "missing"));
  }, [matrix, missingOnly]);

  const allExtraDocs = useMemo(
    () => [...new Set((matrix?.rows ?? []).flatMap((r) => r.extra_docs))],
    [matrix],
  );

  const sendReminder = async (clientId: string) => {
    if (!id) return;
    setBusyClient(clientId);
    try {
      await api.runReminderSend(id, clientId, 1, "whatsapp");
      // Reload to surface the audit + (eventually) reminder counters.
      await reload();
    } catch {
      setError("שליחת התזכורת נכשלה.");
    } finally {
      setBusyClient(null);
    }
  };

  // ── mid-run client add ───────────────────────────────────────────
  const openAddModal = async () => {
    setError(null);
    setChosenClient(null);
    setAddOpen(true);
    if (allClients === null) {
      try {
        const list = await api.clientsList();
        setAllClients(list.clients);
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : "טעינת לקוחות נכשלה");
      }
    }
  };

  const availableClients = useMemo(() => {
    if (allClients === null || matrix === null) return [];
    const inRun = new Set(matrix.rows.map((r) => r.client_id));
    return allClients.filter((c) => !inRun.has(c.client_id));
  }, [allClients, matrix]);

  const submitAdd = async () => {
    if (!id || !chosenClient) return;
    setAdding(true);
    try {
      await api.inviteCreate(id, chosenClient);
      setAddOpen(false);
      setChosenClient(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "הוספת לקוח נכשלה");
    } finally {
      setAdding(false);
    }
  };


  const exportCsv = async () => {
    if (!id) return;
    try {
      const url = await api.runExportCsvUrl(id);
      // Trigger a download via a temporary anchor. Object-URL stays valid
      // until next-tick revoke, plenty of time for the browser to start
      // the download.
      const a = document.createElement("a");
      a.href = url;
      a.download = `run-${id.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("ייצוא ה־CSV נכשל.");
    }
  };

  if (error) return <Text c="red">{error}</Text>;
  if (!id) return <Text c="red">חסר run_id.</Text>;
  if (matrix === null)
    return (
      <Group py="lg">
        <Loader size="sm" />
      </Group>
    );

  const overdue = new Date(matrix.deadline_at) < new Date();

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={4}>
          <Title order={2}>{campaignName ?? matrix.period_label}</Title>
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              דדליין: {new Date(matrix.deadline_at).toLocaleDateString("he-IL")}
            </Text>
            {overdue && <Badge color="red">פג תוקף</Badge>}
            {meta?.closed_at && (
              <Badge color="gray" variant="filled">סגור</Badge>
            )}
          </Group>
        </Stack>
        <Group>
          <Badge color="green" size="lg">
            התקבלו: {matrix.summary.received}
          </Badge>
          <Badge color="red" size="lg">
            חסרים: {matrix.summary.missing}
          </Badge>
          <Badge color="blue" size="lg" variant="light">
            לקוחות: {matrix.summary.clients}
          </Badge>
        </Group>
      </Group>

      <Group justify="space-between">
        <Switch
          checked={missingOnly}
          onChange={(e) => setMissingOnly(e.currentTarget.checked)}
          label="הצג רק חסרים"
        />
        <Group gap={6}>
          <Button variant="light" onClick={openAddModal}>
            + הוסף לקוח לקמפיין
          </Button>
          <Button variant="light" onClick={exportCsv}>
            ייצוא CSV
          </Button>
        </Group>
      </Group>

      {matrix.rows.length === 0 ? (
        <Alert color="yellow">
          אין לקוחות מוזמנים לקמפיין זה עדיין. צור הזמנות מ־<Text component="span" c="blue">/invite</Text>.
        </Alert>
      ) : visibleRows.length === 0 ? (
        <Alert color="green">כל המסמכים התקבלו 🎉</Alert>
      ) : (
        <Paper withBorder style={{ overflowX: "auto" }}>
          <Table striped highlightOnHover style={{ minWidth: "max-content" }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ position: "sticky", right: 0, background: "var(--mantine-color-body)", zIndex: 1 }}>לקוח</Table.Th>
                {matrix.document_labels.map((l) => (
                  <Table.Th key={l} dir="rtl" style={{ whiteSpace: "nowrap" }}>
                    {l}
                  </Table.Th>
                ))}
                {allExtraDocs.map((l) => (
                  <Table.Th
                    key={`extra-${l}`}
                    dir="rtl"
                    style={{ whiteSpace: "nowrap", fontStyle: "italic", color: "var(--mantine-color-dimmed)" }}
                  >
                    {l}
                  </Table.Th>
                ))}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visibleRows.map((row) => {
                const allDone = row.cells.every(
                  (c) => c.status === "acked" || c.status === "archived",
                );
                return (
                  <Table.Tr key={row.client_id}>
                    <Table.Td style={{ position: "sticky", right: 0, background: "var(--mantine-color-body)", zIndex: 1, whiteSpace: "nowrap" }}>{row.client_name}</Table.Td>
                    {row.cells.map((c) => (
                      <Table.Td key={c.document_label}>
                        <Badge
                          color={STATUS_COLORS[c.status]}
                          variant={c.status === "missing" ? "outline" : "light"}
                          title={
                            c.uploaded_at
                              ? `הועלה ב־${new Date(c.uploaded_at).toLocaleString("he-IL")}`
                              : "טרם הועלה"
                          }
                        >
                          {STATUS_LABELS[c.status]}
                        </Badge>
                      </Table.Td>
                    ))}
                    {allExtraDocs.map((doc) => (
                      <Table.Td key={`extra-${doc}`}>
                        {row.extra_docs.includes(doc) ? (
                          <Badge color="red" variant="outline">חסר</Badge>
                        ) : (
                          <Text size="xs" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                    ))}
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      <Group gap={6}>
                        <Button
                          size="xs"
                          variant="light"
                          component={Link}
                          to={`/clients/${row.client_id}`}
                        >
                          כרטיס לקוח
                        </Button>
                        {!allDone && (
                          <Button
                            size="xs"
                            variant="light"
                            loading={busyClient === row.client_id}
                            onClick={() => sendReminder(row.client_id)}
                          >
                            שלח תזכורת
                          </Button>
                        )}
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          aria-label="הגדרות"
                          title="הגדרות לקוח בקמפיין זה"
                          onClick={() =>
                            setSettingsClient({
                              client_id: row.client_id,
                              client_name: row.client_name,
                            })
                          }
                        >
                          ⚙
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Mid-run client add */}
      <Modal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title="הוסף לקוח לקמפיין זה"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            הזמנה תישלח אוטומטית לכתובת האימייל של הלקוח עם הקישור להעלאה.
            אם ללקוח אין אימייל בכרטיס — הוסף/י אותו ראשית בכרטיסיית
            "לקוחות".
          </Text>
          {allClients === null ? (
            <Loader size="sm" />
          ) : availableClients.length === 0 ? (
            <Alert color="yellow">
              כל הלקוחות שלך כבר נמצאים בקמפיין זה. הוסף/י קודם לקוח חדש
              ב־"לקוחות".
            </Alert>
          ) : (
            <>
              <Select
                label="בחר לקוח"
                placeholder="הקלד שם או בחר מהרשימה"
                data={availableClients.map((c) => ({
                  value: c.client_id,
                  label: c.email ? `${c.name} — ${c.email}` : c.name,
                }))}
                value={chosenClient}
                onChange={setChosenClient}
                searchable
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setAddOpen(false)}>
                  ביטול
                </Button>
                <Button
                  onClick={submitAdd}
                  loading={adding}
                  disabled={!chosenClient}
                >
                  הוסף ושלח הזמנה
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      {/* Per-(run, client) settings */}
      {settingsClient && id && (
        <InviteSettingsModal
          runId={id}
          clientId={settingsClient.client_id}
          clientName={settingsClient.client_name}
          campaignDefaultSchedule={campaignDefaultSchedule}
          onClose={() => setSettingsClient(null)}
          onRemoved={() => { setSettingsClient(null); reload(); }}
          onDocsSaved={reload}
        />
      )}
    </Stack>
  );
}

function InviteSettingsModal({
  runId,
  clientId,
  clientName,
  campaignDefaultSchedule,
  onClose,
  onRemoved,
  onDocsSaved,
}: {
  runId: string;
  clientId: string;
  clientName: string;
  campaignDefaultSchedule: number[] | null;
  onClose: () => void;
  onRemoved: () => void;
  onDocsSaved: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  // true when no client-level override exists — selection reflects the inherited campaign default
  const [scheduleInherited, setScheduleInherited] = useState(true);
  const [savingPause, setSavingPause] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [extraDocs, setExtraDocs] = useState<string[]>([]);
  const [savingDocs, setSavingDocs] = useState(false);
  const [applyAllConfirm, setApplyAllConfirm] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [clientFallback, setClientFallback] = useState<{
    paused: boolean;
    schedule: number[] | null;
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.runClientCollection(runId, clientId);
        setInviteId(data.invite_id);
        setPaused(data.invite_reminders_paused ?? false);
        const customDays = data.invite_reminder_schedule_days;
        if (customDays != null) {
          setSelectedDays(customDays);
          setScheduleInherited(false);
        } else {
          // No override — show the effective (campaign) default as pre-selected
          setSelectedDays(campaignDefaultSchedule ?? []);
          setScheduleInherited(true);
        }
        setExtraDocs(data.extra_required_docs ?? []);
        setClientFallback({
          paused: data.reminders_paused,
          schedule: data.reminder_schedule_days,
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : "טעינה נכשלה");
      } finally {
        setLoading(false);
      }
    })();
  }, [runId, clientId]);

  const savePause = async (next: boolean) => {
    if (!inviteId) return;
    setSavingPause(true);
    try {
      await api.inviteSetRemindersPaused(inviteId, next);
      setPaused(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שמירת השהיה נכשלה");
    } finally {
      setSavingPause(false);
    }
  };

  const toggleDay = (day: number) => {
    setScheduleInherited(false);
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => b - a),
    );
  };

  const resetSchedule = () => {
    setSelectedDays(campaignDefaultSchedule ?? []);
    setScheduleInherited(true);
  };

  const saveSchedule = async () => {
    if (!inviteId) return;
    setSavingSchedule(true);
    try {
      // send null when inherited = "use campaign default"; explicit array when overridden
      const resp = await api.inviteSetReminderSchedule(
        inviteId, scheduleInherited ? null : selectedDays.length > 0 ? selectedDays : null,
      );
      if (resp.reminder_schedule_days != null) {
        setSelectedDays(resp.reminder_schedule_days);
        setScheduleInherited(false);
      } else {
        setSelectedDays(campaignDefaultSchedule ?? []);
        setScheduleInherited(true);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שמירת לוח זמנים נכשלה");
    } finally {
      setSavingSchedule(false);
    }
  };

  const saveExtraDocs = async (applyToAll = false) => {
    if (!inviteId) return;
    setSavingDocs(true);
    try {
      const resp = await api.inviteSetExtraDocs(inviteId, extraDocs);
      setExtraDocs(resp.extra_required_docs);
      if (applyToAll) {
        setApplyingAll(true);
        await api.clientSetExtraDocsAllRuns(clientId, extraDocs);
        setApplyingAll(false);
      }
      setApplyAllConfirm(false);
      onDocsSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שמירת מסמכים נכשלה");
    } finally {
      setSavingDocs(false);
    }
  };

  const removeFromCampaign = async () => {
    if (!inviteId) return;
    setRemoving(true);
    try {
      await api.inviteRemoveSoft(inviteId);
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "הסרה נכשלה");
      setRemoving(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`הגדרות עבור ${clientName}`}
      size="xl"
    >
      {loading ? (
        <Loader size="sm" />
      ) : error ? (
        <Alert color="red">{error}</Alert>
      ) : !inviteId ? (
        <Alert color="yellow">
          לקוח זה לא הוזמן עדיין לקמפיין זה. סגור והוסף קודם דרך
          "+ הוסף לקוח לקמפיין".
        </Alert>
      ) : (
        <ScrollArea.Autosize mah="85vh" offsetScrollbars>
        <Stack gap="md" pb="xs">
          {/* ── Pause reminders ── */}
          <Paper p="sm" withBorder>
            <Stack gap={4}>
              <Switch
                checked={paused}
                onChange={(e) => savePause(e.currentTarget.checked)}
                label="השהה תזכורות ללקוח זה בקמפיין זה"
                disabled={savingPause}
              />
              {clientFallback?.paused && !paused && (
                <Text size="xs" c="orange.9">
                  ⚠️ הלקוח מושהה ברמה כללית — תזכורות ממילא לא יישלחו אליו.
                </Text>
              )}
            </Stack>
          </Paper>

          {/* ── Custom reminder schedule ── */}
          <Paper p="sm" withBorder>
            <Stack gap={6}>
              <Group justify="space-between" align="flex-end">
                <div>
                  <Text size="sm" fw={500}>מתי לשלוח תזכורות?</Text>
                  {scheduleInherited && (
                    <Text size="xs" c="dimmed">ירושה מהקמפיין — לחץ על יום כדי להגדיר בנפרד</Text>
                  )}
                </div>
                {!scheduleInherited && (
                  <Button size="xs" variant="subtle" color="gray" onClick={resetSchedule}>
                    אפס לברירת מחדל
                  </Button>
                )}
              </Group>
              <Group gap={8}>
                {REMINDER_DAY_OPTIONS.map((day) => (
                  <Button
                    key={day}
                    size="xs"
                    variant={selectedDays.includes(day) ? "filled" : "light"}
                    color={scheduleInherited ? "gray" : "blue"}
                    onClick={() => toggleDay(day)}
                    style={{ minWidth: 52 }}
                  >
                    {day} {day === 1 ? "יום" : "ימים"}
                  </Button>
                ))}
              </Group>
              {scheduleInherited ? (
                <Text size="xs" c="dimmed">
                  {campaignDefaultSchedule
                    ? `ברירת מחדל של הקמפיין: ${campaignDefaultSchedule.slice().sort((a, b) => b - a).join(", ")} ימים לפני הדדליין`
                    : "ברירת מחדל של הקמפיין"}
                </Text>
              ) : selectedDays.length === 0 ? (
                <Text size="xs" c="orange">לא נבחרו ימים — תזכורות לא יישלחו</Text>
              ) : (
                <Text size="xs" c="blue">
                  תזכורות אישיות: {selectedDays.slice().sort((a, b) => b - a).join(", ")} ימים לפני הדדליין
                </Text>
              )}
              <Group justify="flex-end">
                <Button size="xs" variant="light" loading={savingSchedule} onClick={saveSchedule}>
                  שמור
                </Button>
              </Group>
            </Stack>
          </Paper>

          {/* ── Extra docs for this client only ── */}
          <Paper p="sm" withBorder>
            <Stack gap={4}>
              <TagsInput
                label="מסמכים נוספים ללקוח זה בלבד"
                description="מסמכים שרק לקוח זה צריך להגיש בקמפיין זה"
                data={EXTRA_DOC_SUGGESTIONS}
                value={extraDocs}
                onChange={setExtraDocs}
                placeholder="בחר מהרשימה או הקלד שם חופשי ולחץ Enter"
                splitChars={[","]}
                maxDropdownHeight={300}
                dir="rtl"
              />
              <Group justify="flex-end">
                <Button size="xs" variant="light" loading={savingDocs} onClick={() => setApplyAllConfirm(true)}>
                  שמור מסמכים
                </Button>
              </Group>
            </Stack>
          </Paper>

          {/* ── Apply-to-all confirmation ── */}
          <Modal
            opened={applyAllConfirm}
            onClose={() => setApplyAllConfirm(false)}
            title="שמירת מסמכים נוספים"
            size="sm"
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                האם להוסיף מסמכים אלו לכל הקמפיינים שהלקוח <Text component="span" fw={600}>{clientName}</Text> משתתף בהם?
              </Text>
              <Text size="xs" c="dimmed">
                {extraDocs.length > 0
                  ? extraDocs.join(" | ")
                  : "לא נבחרו מסמכים"}
              </Text>
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" color="gray" onClick={() => saveExtraDocs(false)} loading={savingDocs && !applyingAll}>
                  רק לקמפיין זה
                </Button>
                <Button onClick={() => saveExtraDocs(true)} loading={applyingAll}>
                  כן, לכל הקמפיינים
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* ── Remove from campaign ── */}
          <Paper p="sm" withBorder style={{ borderColor: "var(--mantine-color-red-3)" }}>
            <Stack gap={6}>
              <Text size="sm" fw={500} c="red">הסרה מהקמפיין</Text>
              <Text size="xs" c="dimmed">
                הסרה תמנע מהלקוח לבצע העלאות נוספות. הקבצים שהועלו עד כה יישמרו.
              </Text>
              <Group justify="flex-end">
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  loading={removing}
                  onClick={removeFromCampaign}
                >
                  הסר מהקמפיין
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>
        </ScrollArea.Autosize>
      )}
    </Modal>
  );
}
